export type BrandScheme = 'standard' | 'classic';
export interface Appearance {
    scheme: BrandScheme;
    menuHighlight: 'border' | 'filled';
    displayName: string;
    color: string;
    logo: string;
    colors: Record<BrandScheme, string>;
}
export interface Billing {
    country?: string;
    vat?: string;
    invoiceEmail?: string;
    commercialName?: string;
    commercialEmail?: string;
    commercialPhone?: string;
    technicalName?: string;
    technicalEmail?: string;
    technicalPhone?: string;
    companyName: string;
    street: string;
    zipCode: string;
    city: string;
}
export const DEFAULT_APPEARANCE: Appearance = {
    scheme: 'standard',
    menuHighlight: 'border',
    displayName: '',
    color: '#1286CA',
    logo: '',
    colors: { standard: '#1286CA', classic: '#D99515' },
};
export const validColor = (value: unknown): value is string =>
    typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
export function normalizeAppearance(value: Partial<Appearance> = {}): Appearance {
    const scheme = value.scheme === 'classic' ? 'classic' : 'standard';
    const colors = { ...DEFAULT_APPEARANCE.colors };
    for (const key of ['standard', 'classic'] as const)
        if (validColor(value.colors?.[key])) colors[key] = value.colors[key];
    const color = validColor(value.color) ? value.color : colors[scheme];
    colors[scheme] = color;
    return {
        scheme,
        colors,
        color,
        menuHighlight: value.menuHighlight === 'filled' ? 'filled' : 'border',
        displayName: typeof value.displayName === 'string' ? value.displayName.trim().slice(0, 200) : '',
        logo:
            typeof value.logo === 'string' &&
            value.logo.length < 1500000 &&
            /^data:image\/(png|jpeg|webp);base64,/.test(value.logo)
                ? value.logo
                : '',
    };
}
export function rgb(hex: string): number[] {
    return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
}
export function mix(hex: string, target: number, amount: number): string {
    return (
        '#' +
        rgb(hex)
            .map((c) =>
                Math.round(c + (target - c) * amount)
                    .toString(16)
                    .padStart(2, '0')
            )
            .join('')
    );
}
export function luminance(hex: string): number {
    const c = rgb(hex).map((v) => {
        v /= 255;
        return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return c[0] * 0.2126 + c[1] * 0.7152 + c[2] * 0.0722;
}
export function inkOn(hex: string): string {
    return luminance(hex) > 0.179 ? '#000000' : '#ffffff';
}
export function contrastInk(hex: string, dark: boolean): string {
    let ink = hex;
    const bg = dark ? '#1e293b' : '#ffffff';
    for (let i = 0; i < 20; i++) {
        const a = luminance(ink),
            b = luminance(bg);
        if ((Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05) >= 4.5) break;
        ink = mix(ink, dark ? 255 : 0, 0.12);
    }
    return ink;
}
